import { CreateReportButton } from "@/components/CreateReportButton";
import { PageActions, scrollToAiBlock } from "@/components/PageActions";
import { LastImportInfo } from "@/components/LastImportInfo";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toParams, eur, pct } from "@/lib/kpi";
import UnifiedFilters from "@/components/UnifiedFilters";
import { Trophy, TrendingUp, Flame, Award, Star, Zap, ChevronDown } from "lucide-react";
import WasaScoreRanking from "@/components/WasaScoreRanking";
import KpiRankingV2 from "./KpiRankingV2";

const METRICS = [
  { key: "ca_day",       label: "CA Jour",       unit: "€",  fmt: eur, icon: Zap,        gradient: "from-yellow-400 via-orange-500 to-red-600" },
  { key: "ca_month",     label: "CA Mensuel",    unit: "€",  fmt: eur, icon: Trophy,     gradient: "from-cyan-400 via-blue-500 to-indigo-700" },
  { key: "ca_year",      label: "CA Cumulé",     unit: "€",  fmt: eur, icon: Award,      gradient: "from-emerald-400 via-green-500 to-teal-700" },
  { key: "perf_obj",     label: "Perf vs Obj",   unit: "%",  fmt: (v) => `${(v||0).toFixed(1)}%`, icon: TrendingUp, gradient: "from-fuchsia-500 via-pink-500 to-rose-600" },
  { key: "evolution_n1", label: "Évolution N-1", unit: "%",  fmt: (v) => `${(v||0).toFixed(1)}%`, icon: Flame,      gradient: "from-orange-400 via-red-500 to-fuchsia-700" },
  { key: "wasa_score",   label: "Wasa Score",    unit: "/100",fmt: (v) => `${Math.round(v||0)}`,   icon: Star,       gradient: "from-purple-500 via-violet-500 to-blue-700" },
];

const SCOPES = [
  { key: "corner",  label: "Corners" },
  { key: "bm",      label: "BM" },
  { key: "dr",      label: "DR" },
  { key: "secteur", label: "Secteur" },
];

function Odometer({ value, formatter }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = Date.now(); const duration = 1200; const from = display; const to = value || 0;
    const anim = () => {
      const t = Math.min((Date.now() - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) requestAnimationFrame(anim);
    };
    requestAnimationFrame(anim);
    // eslint-disable-next-line
  }, [value]);
  return <span className="tabular-nums">{formatter ? formatter(display) : Math.round(display)}</span>;
}

function Confetti() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {Array.from({ length: 24 }).map((_, i) => (
        <span key={i}
          className="absolute w-1.5 h-3 rounded-sm animate-[confetti_2.5s_ease-out_forwards]"
          style={{
            left: `${Math.random() * 100}%`, top: "-10%",
            background: ["#f59e0b","#ec4899","#3b82f6","#10b981","#8b5cf6","#f97316"][i % 6],
            animationDelay: `${Math.random() * 0.8}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}/>
      ))}
      <style>{`@keyframes confetti { to { transform: translateY(120vh) rotate(720deg); opacity: 0; } }`}</style>
    </div>
  );
}

function PodiumCard({ item, position, metric }) {
  const M = METRICS.find(m => m.key === metric) || METRICS[1];
  const heights = { 1: "h-52", 2: "h-40", 3: "h-32" };
  const glows = {
    1: "shadow-[0_0_60px_-5px_rgba(251,191,36,0.6)] border-yellow-400/50",
    2: "shadow-[0_0_40px_-5px_rgba(148,163,184,0.5)] border-slate-300/50",
    3: "shadow-[0_0_40px_-5px_rgba(217,119,6,0.5)] border-amber-700/50",
  };
  const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
  return (
    <div className={`relative ${heights[position]} rounded-2xl bg-gradient-to-br ${M.gradient} p-4 border-2 ${glows[position]} transition-transform hover:-translate-y-1 hover:scale-[1.02] overflow-hidden`}
         data-testid={`podium-${position}`}>
      {position === 1 && <Confetti/>}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.3),transparent_60%)]"/>
      <div className="relative flex flex-col h-full text-white">
        <div className="flex items-center justify-between">
          <span className="text-4xl">{medals[position]}</span>
          <span className="text-[10px] font-bold uppercase tracking-widest opacity-80 bg-white/20 backdrop-blur px-2 py-0.5 rounded">
            #{position}
          </span>
        </div>
        <div className="mt-auto">
          <div className="font-display font-black text-xl leading-tight truncate drop-shadow-lg">{item?.name || "—"}</div>
          <div className="text-[10px] opacity-80 mt-0.5 truncate">
            {item?.region || ""}{item?.secteur ? ` · ${item.secteur}` : ""}
          </div>
          <div className="font-display font-black text-2xl mt-1 drop-shadow-lg">
            <Odometer value={item?.[metricField(metric)] || 0} formatter={M.fmt}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function metricField(metric) {
  return {
    ca_day: "ca_realise", ca_month: "ca_realise", ca_year: "ca_realise",
    perf_obj: "perf_obj", evolution_n1: "evolution_n1", wasa_score: "score",
  }[metric] || "ca_realise";
}

function positionSuffix(n) {
  if (n === 1) return "1er";
  return `${n}${n === 2 ? "e" : "e"}`;
}

export default function KpiScoresRanking() {
  const [tab, setTab] = useState("wasa");
  const [scope, setScope] = useState("corner");
  const [metric, setMetric] = useState("ca_month");
  const [filters, setFilters] = useState(null);
  const [f, setF] = useState({ annees: [2026], year_mode: "civil" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/kpi/filters").then(r => setFilters(r.data)); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/kpi/rankings", { params: { ...toParams(f), scope, metric } })
      .then(r => setRows(r.data.items || []))
      .finally(() => setLoading(false));
  }, [f, scope, metric]);

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);
  const currentMetric = METRICS.find(m => m.key === metric) || METRICS[1];
  const Icon = currentMetric.icon;

  return (
    <div className="space-y-6" data-testid="kpi-scores-ranking-page">
      <div>
        <h1 className="font-display text-4xl font-black tracking-tight text-slate-900">Wasa Score & Ranking</h1>
        <p className="text-slate-500 mt-1">Score /100 & classements compétitifs par corner, BM, DR ou secteur.</p>
        <LastImportInfo className="mt-2"/><CreateReportButton module="ca_daily" className="mt-2 ml-2"/>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
        <button data-testid="tab-wasa" onClick={() => setTab("wasa")}
                className={`px-5 py-2 rounded-lg font-bold text-sm transition-all ${tab === "wasa" ? "bg-gradient-to-r from-purple-500 to-blue-600 text-white shadow-lg" : "text-slate-600 hover:bg-slate-50"}`}>
          <Star className="w-4 h-4 inline mr-1.5"/> Wasa Score
        </button>
        <button data-testid="tab-ranking" onClick={() => setTab("ranking")}
                className={`px-5 py-2 rounded-lg font-bold text-sm transition-all ${tab === "ranking" ? "bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg" : "text-slate-600 hover:bg-slate-50"}`}>
          <Trophy className="w-4 h-4 inline mr-1.5"/> Ranking
        </button>
      </div>
      <PageActions title="Wasa Score & Ranking" onAi={scrollToAiBlock} testid="scores-actions" />
      </div>

      {tab === "wasa" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <WasaScoreRanking/>
        </div>
      )}

      {tab === "ranking" && <KpiRankingV2/>}
      {tab === "ranking_old_hidden" && (
        <>
          <UnifiedFilters filters={filters} value={f} onChange={setF} showDate showWeek={false}/>

          {/* Scope toggle */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Classement par</span>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              {SCOPES.map(s => (
                <button key={s.key} data-testid={`scope-${s.key}`} onClick={() => setScope(s.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${scope === s.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Metric chips */}
          <div className="flex flex-wrap gap-2">
            {METRICS.map(m => {
              const active = metric === m.key;
              const Ic = m.icon;
              return (
                <button key={m.key} data-testid={`metric-${m.key}`} onClick={() => setMetric(m.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                          active ? `bg-gradient-to-r ${m.gradient} text-white shadow-lg scale-105` : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}>
                  <Ic className="w-3.5 h-3.5"/> {m.label}
                </button>
              );
            })}
          </div>

          {/* Podium */}
          {loading ? (
            <div className="text-center py-12 text-slate-400">Calcul du classement...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Aucune donnée pour ces filtres.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="pt-8"><PodiumCard item={top3[1]} position={2} metric={metric}/></div>
                <div><PodiumCard item={top3[0]} position={1} metric={metric}/></div>
                <div className="pt-16"><PodiumCard item={top3[2]} position={3} metric={metric}/></div>
              </div>

              {/* Full list */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className={`px-4 py-3 bg-gradient-to-r ${currentMetric.gradient} text-white flex items-center gap-2`}>
                  <Icon className="w-4 h-4"/>
                  <span className="font-display font-bold text-sm uppercase tracking-widest">Classement complet · {currentMetric.label}</span>
                  <span className="ml-auto text-xs opacity-90">{rows.length} {SCOPES.find(s => s.key === scope)?.label.toLowerCase()}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {rest.map(r => (
                    <div key={r.name} data-testid={`rank-row-${r.rank}`}
                         className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                      <div className="w-10 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-black tabular-nums ${
                          r.rank <= 10 ? "bg-blue-100 text-blue-700" : r.rank <= 50 ? "bg-slate-100 text-slate-600" : "bg-white text-slate-400"
                        }`}>{positionSuffix(r.rank)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-900 truncate">{r.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {r.region || ""}{r.secteur && scope === "corner" ? ` · ${r.secteur}` : ""}{r.bm && scope === "corner" ? ` · ${r.bm}` : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-display font-black text-sm tabular-nums bg-gradient-to-r ${currentMetric.gradient} bg-clip-text text-transparent`}>
                          {currentMetric.fmt(r[metricField(metric)] || 0)}
                        </div>
                        {metric.startsWith("ca_") && r.perf_obj != null && (
                          <div className={`text-[9px] font-bold ${r.perf_obj >= 100 ? "text-emerald-600" : "text-rose-500"}`}>
                            {r.perf_obj.toFixed(1)}% obj
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
